import {
  normalizeAssistantPresentation,
} from '@triedge/ai-assistant-ui/utils/assistantPresentation';
import type {
  AssistantPresentationActionLink,
  AssistantPresentationArtifactItem,
  AssistantPresentationChartItem,
  AssistantPresentationChecklistItem,
  AssistantPresentationMetricItem,
  AssistantPresentationModel,
  AssistantPresentationPaginationConfig,
  AssistantPresentationResultCard,
  AssistantPresentationSection,
  AssistantPresentationSectionType,
  AssistantPresentationTableColumn,
  AssistantPresentationTableRow,
  AssistantResponseKind,
} from '@triedge/ai-assistant-ui/types';
import { resolveAssistantRouteFamily } from '../config/ai-assistant/route-family-registry';
import {
  buildAssistantLinkGroups,
  buildAssistantResultCards,
  buildAssistantSources,
  splitAssistantResponseSections,
  stripInlineAssistantLinks,
} from '../components/ai/assistantResponsePresentation';
import { buildAdmissionsAssistantPresentationModel } from '../components/ai/assistantPresentationModel';
import { projectAdmissionsResponseSurface } from '../components/ai/assistantResponseSurfaceProjector';
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantPresentationDiagnostics,
  AdmissionsAssistantTaskProposalCard,
  AdmissionsAssistantTaskThreadRollup,
  AdmissionsAssistantTaskThreadStatus,
  AdmissionsCitation,
} from '../components/ai/types';
import type { AssistantSettingsSpecialistSnapshot } from '../components/ai/assistantSettingsSpecialist';

export type AssistantPresentationFactMetric = Pick<
  AssistantPresentationMetricItem,
  'id' | 'label' | 'value' | 'detail' | 'tone'
>;

export type AssistantPresentationFactCheck = Pick<
  AssistantPresentationChecklistItem,
  'id' | 'label' | 'detail' | 'status'
>;

export type AssistantPresentationFactTarget = Pick<
  AssistantPresentationResultCard,
  'id' | 'label' | 'eyebrow' | 'badge' | 'meta' | 'preview' | 'detailRows'
> & {
  href?: string | null;
  actionLabel?: string | null;
  secondaryHref?: string | null;
  secondaryActionLabel?: string | null;
};

export type AssistantPresentationFactTable = {
  id?: string | null;
  title?: string | null;
  columns: AssistantPresentationTableColumn[];
  rows: AssistantPresentationTableRow[];
  pagination?: AssistantPresentationPaginationConfig | null;
  footerText?: string | null;
  footerAction?: AssistantPresentationActionLink | null;
};

export type AssistantPresentationFactChart = {
  id?: string | null;
  title?: string | null;
  chartTitle?: string | null;
  items: AssistantPresentationChartItem[];
  maxValue?: number | null;
};

export type AssistantPresentationUnavailableFacts = {
  reason: string;
  prerequisites: AssistantPresentationFactCheck[];
};

export type AssistantPresentationFactBundle = {
  metrics?: AssistantPresentationFactMetric[];
  checks?: AssistantPresentationFactCheck[];
  targets?: AssistantPresentationFactTarget[];
  table?: AssistantPresentationFactTable | null;
  chart?: AssistantPresentationFactChart | null;
  body?: string | null;
  unavailable?: AssistantPresentationUnavailableFacts | null;
  artifacts?: AssistantPresentationArtifactItem[];
  workspace?: AssistantPresentationArtifactItem[];
};

export type AssistantPresentationCompositionInput = {
  answer: string;
  citations: AdmissionsCitation[];
  actions?: AdmissionsAIContextualAction[];
  module: string;
  routeFamilyId?: string | null;
  pathname?: string | null;
  capabilityBinding?: {
    capabilityId?: string | null;
    scenarioKey?: string | null;
    analysisScope?: string | null;
  } | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
  taskProposal?: AdmissionsAssistantTaskProposalCard | null;
  taskThread?: AdmissionsAssistantTaskThreadStatus | null;
  taskThreadRollup?: AdmissionsAssistantTaskThreadRollup | null;
  diagnostics?: AdmissionsAssistantPresentationDiagnostics | null;
  readOnlyDegradedReason?: string | null;
  settingsSpecialistSnapshot?: AssistantSettingsSpecialistSnapshot | null;
  projectedSections?: AssistantPresentationSection[];
  suppressRelatedLinks?: boolean;
  debug?: Record<string, unknown> | null;
  facts?: AssistantPresentationFactBundle | null;
  clientProjectionUsed?: boolean;
};

export type AssistantPresentationCompositionResult = {
  presentation: AssistantPresentationModel;
  responseKind: AssistantResponseKind;
  visibleSectionTypes: AssistantPresentationSectionType[];
  supportSectionTypes: AssistantPresentationSectionType[];
  contractDiagnostics: Record<string, unknown>;
  clientProjectionUsed: boolean;
};

const SUPPORT_SECTION_TYPES = new Set<AssistantPresentationSectionType>([
  'sources',
  'related_links',
  'diagnostics',
]);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const compactText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const GENERIC_CHECKLIST_HEADING_PATTERN =
  /^(?:#{1,6}\s*)?(?:recommended\s+next\s+checks?|next\s+steps?|recommended\s+follow-?up|action\s+items?)\s*:?\s*/i;

const stripDuplicatedChecklistHeading = (value: string): string => {
  const compacted = compactText(value);
  if (!compacted) {
    return '';
  }
  const withoutHeading = compactText(compacted.replace(GENERIC_CHECKLIST_HEADING_PATTERN, ''));
  return withoutHeading || 'Actionable checks are listed below.';
};

const firstSentence = (value: string): string => {
  const normalized = compactText(value);
  if (!normalized) {
    return '';
  }
  const match = normalized.match(/^(.+?[.!?])(?:\s|$)/);
  return compactText(match?.[1] ?? normalized);
};

const stripChecklistBlob = (value: string): string => {
  const normalized = String(value ?? '')
    .replace(/([a-z)])(\d+\.)/g, '$1\n$2')
    .replace(/\s+(\d+\.)\s+/g, '\n$1 ')
    .trim();
  const firstNumberedIndex = normalized.search(/(?:^|\n)\s*\d+\.\s+/);
  if (firstNumberedIndex >= 0) {
    return stripDuplicatedChecklistHeading(normalized.slice(0, firstNumberedIndex))
      || 'Actionable checks are listed below.';
  }
  return stripDuplicatedChecklistHeading(normalized);
};

const sectionTypes = (sections: AssistantPresentationSection[]): AssistantPresentationSectionType[] => (
  Array.from(new Set(sections.map((section) => section.type)))
);

const readRecordArray = (value: unknown): Record<string, unknown>[] => (
  Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> =>
      Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry))
    : []
);

const readNumber = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const csvEscape = (value: unknown): string => {
  const text = compactText(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const encodeDataUrl = (mimeType: string, value: string): string =>
  `data:${mimeType};charset=utf-8,${encodeURIComponent(value)}`;

const tableCellText = (cell: unknown): string => (
  isRecord(cell) ? compactText(cell.text) : compactText(cell)
);

const tableRowsToRecords = (
  columns: AssistantPresentationTableColumn[],
  rows: AssistantPresentationTableRow[],
): Array<Record<string, string>> => rows.map((row) => columns.reduce<Record<string, string>>((record, column, columnIndex) => ({
  ...record,
  [column.id]: tableCellText(row.cells[columnIndex]),
}), {}));

const encodeTableCsvDataUrl = (
  columns: AssistantPresentationTableColumn[],
  rows: AssistantPresentationTableRow[],
): string => {
  const headers = columns.map((column) => column.id);
  const records = tableRowsToRecords(columns, rows);
  const lines = [
    headers.map(csvEscape).join(','),
    ...records.map((record) => headers.map((header) => csvEscape(record[header])).join(',')),
  ];
  return encodeDataUrl('text/csv', lines.join('\n'));
};

const encodeJsonDataUrl = (value: unknown): string =>
  encodeDataUrl('application/json', JSON.stringify(value, null, 2));

const normalizePipelineStageLabel = (value: unknown): string => (
  compactText(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (token) => token.toUpperCase())
);

const resolveVisibleSectionTypes = (sections: AssistantPresentationSection[]): AssistantPresentationSectionType[] => (
  sectionTypes(sections).filter((type) => !SUPPORT_SECTION_TYPES.has(type))
);

const resolveSupportSectionTypes = (sections: AssistantPresentationSection[]): AssistantPresentationSectionType[] => (
  sectionTypes(sections).filter((type) => SUPPORT_SECTION_TYPES.has(type))
);

const normalizeMetricFacts = (
  metrics: AssistantPresentationFactMetric[] | undefined,
): AssistantPresentationMetricItem[] => (
  (metrics ?? [])
    .map((metric, index) => ({
      id: compactText(metric.id) || `metric-${index + 1}`,
      label: compactText(metric.label),
      value: compactText(metric.value),
      detail: compactText(metric.detail) || null,
      tone: metric.tone,
    }))
    .filter((metric) => metric.label && metric.value)
);

const normalizeCheckFacts = (
  checks: AssistantPresentationFactCheck[] | undefined,
): AssistantPresentationChecklistItem[] => (
  (checks ?? [])
    .map((check, index) => ({
      id: compactText(check.id) || `check-${index + 1}`,
      label: compactText(check.label),
      detail: compactText(check.detail) || null,
      status: check.status,
    }))
    .filter((check) => check.label)
);

const normalizeTargetFacts = (
  targets: AssistantPresentationFactTarget[] | undefined,
): AssistantPresentationResultCard[] => (
  (targets ?? [])
    .map((target, index) => {
      const href = compactText(target.href);
      const secondaryHref = compactText(target.secondaryHref);
      return {
        id: compactText(target.id) || `target-${index + 1}`,
        label: compactText(target.label),
        eyebrow: compactText(target.eyebrow) || null,
        badge: compactText(target.badge) || null,
        meta: compactText(target.meta) || null,
        preview: compactText(target.preview) || null,
        primaryAction: href
          ? {
              label: compactText(target.actionLabel) || 'Open',
              href,
            }
          : null,
        secondaryAction: secondaryHref
          ? {
              label: compactText(target.secondaryActionLabel) || 'Open related',
              href: secondaryHref,
            }
          : null,
        detailRows: target.detailRows,
      };
    })
    .filter((target) => target.label)
);

const shouldUseExactTargetCards = (
  debug: Record<string, unknown> | null | undefined,
): boolean => {
  const renderHints = isRecord(debug?.renderHints) ? debug.renderHints : null;
  return renderHints?.resultCardMode === 'exact_targets';
};

const toArtifactSections = (
  facts: AssistantPresentationFactBundle,
): AssistantPresentationSection[] => {
  const items = [
    ...(facts.artifacts ?? []),
    ...(facts.workspace ?? []),
  ].filter((item) => compactText(item.id) && compactText(item.label));
  return items.length > 0
    ? [{
        type: 'artifacts',
        title: 'Artifacts & workspaces',
        items,
      }]
    : [];
};

const buildFactSections = (input: {
  facts: AssistantPresentationFactBundle;
  includeFactTargets: boolean;
}): AssistantPresentationSection[] => {
  const sections: AssistantPresentationSection[] = [];
  const metrics = normalizeMetricFacts(input.facts.metrics);
  if (metrics.length > 0) {
    sections.push({
      type: 'metric_grid',
      title: 'Snapshot',
      items: metrics,
    });
  }

  const unavailableChecks = normalizeCheckFacts(input.facts.unavailable?.prerequisites);
  const checks = normalizeCheckFacts(input.facts.checks);
  const allChecks = [...checks, ...unavailableChecks];
  if (allChecks.length > 0) {
    sections.push({
      type: 'checklist',
      title: input.facts.unavailable ? 'Prerequisites' : 'Recommended next checks',
      items: allChecks,
    });
  }

  const targets = input.includeFactTargets ? normalizeTargetFacts(input.facts.targets) : [];
  if (targets.length > 0) {
    sections.push({
      type: 'result_cards',
      title: 'Applications needing action',
      cards: targets,
    });
  }

  if (input.facts.table?.columns.length && input.facts.table.rows.length) {
    sections.push({
      type: 'table',
      id: input.facts.table.id ?? undefined,
      title: input.facts.table.title ?? null,
      columns: input.facts.table.columns,
      rows: input.facts.table.rows,
      pagination: input.facts.table.pagination ?? null,
      footerText: input.facts.table.footerText ?? null,
      footerAction: input.facts.table.footerAction ?? null,
    });
  }

  if (input.facts.chart?.items.length) {
    sections.push({
      type: 'chart',
      id: input.facts.chart.id ?? undefined,
      title: input.facts.chart.title ?? null,
      chartTitle: input.facts.chart.chartTitle ?? input.facts.chart.title ?? null,
      items: input.facts.chart.items,
      maxValue: input.facts.chart.maxValue ?? Math.max(...input.facts.chart.items.map((item) => item.value), 1),
    });
  }

  sections.push(...toArtifactSections(input.facts));
  if (input.facts.body) {
    sections.push({
      type: 'formatted_body',
      text: input.facts.body,
    });
  }
  return sections;
};

const mergeFacts = (
  explicitFacts: AssistantPresentationFactBundle | null | undefined,
  inferredFacts: AssistantPresentationFactBundle,
): AssistantPresentationFactBundle => ({
  metrics: [...(explicitFacts?.metrics ?? []), ...(inferredFacts.metrics ?? [])],
  checks: [...(explicitFacts?.checks ?? []), ...(inferredFacts.checks ?? [])],
  targets: [...(explicitFacts?.targets ?? []), ...(inferredFacts.targets ?? [])],
  table: explicitFacts?.table ?? inferredFacts.table ?? null,
  chart: explicitFacts?.chart ?? inferredFacts.chart ?? null,
  body: explicitFacts?.body ?? inferredFacts.body ?? null,
  unavailable: explicitFacts?.unavailable ?? inferredFacts.unavailable ?? null,
  artifacts: [...(explicitFacts?.artifacts ?? []), ...(inferredFacts.artifacts ?? [])],
  workspace: [...(explicitFacts?.workspace ?? []), ...(inferredFacts.workspace ?? [])],
});

const numberMetric = (
  id: string,
  label: string,
  value: unknown,
  detail?: string | null,
  tone?: AssistantPresentationMetricItem['tone'],
): AssistantPresentationFactMetric | null => {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? value : null;
  if (numeric == null) {
    return null;
  }
  return {
    id,
    label,
    value: String(numeric),
    detail: detail ?? null,
    tone,
  };
};

const inferFinancialAidFacts = (input: {
  debug?: Record<string, unknown> | null;
  actions?: AdmissionsAIContextualAction[];
  citations: AdmissionsCitation[];
}): AssistantPresentationFactBundle => {
  const queueSummary = isRecord(input.debug?.queueSummary) ? input.debug.queueSummary : null;
  const facts: AssistantPresentationFactBundle = {};
  if (queueSummary) {
    facts.metrics = [
      numberMetric('financial-aid-queue-total', 'Review queue', queueSummary.queueTotal, 'Current financial-aid applications in queue', 'info'),
      numberMetric('financial-aid-loaded-count', 'Loaded records', queueSummary.loadedCount, `Coverage: ${compactText(queueSummary.coverage) || 'unknown'}`, 'neutral'),
      numberMetric('financial-aid-aged-pending', 'Pending 14+ days', queueSummary.agedPendingCount, 'Queue-age signal', 'warning'),
      numberMetric('financial-aid-high-flags', 'High flags', queueSummary.highSeverityFlaggedCount, 'High-severity review flags', 'warning'),
      numberMetric('financial-aid-missing-docs', 'Missing-doc files', queueSummary.applicationsMissingDocuments, 'Applications needing document follow-up', 'warning'),
      numberMetric('financial-aid-pending-appeals', 'Pending appeals', queueSummary.pendingAppeals, 'Appeals blocking award decisions', 'info'),
    ].filter((metric): metric is AssistantPresentationFactMetric => Boolean(metric));

    const available = queueSummary.available !== false;
    const prerequisites: AssistantPresentationFactCheck[] = [];
    if (!available) {
      const reason = compactText(queueSummary.reason) || 'queue_unavailable';
      if (reason === 'partial_coverage') {
        prerequisites.push({
          id: 'financial-aid-prereq-coverage',
          label: 'Increase review-queue coverage or narrow to one application',
          detail: 'The assistant only received a partial queue slice.',
          status: 'blocked',
        });
      } else {
        prerequisites.push({
          id: 'financial-aid-prereq-source',
          label: 'Restore live financial-aid review queue access',
          detail: 'Queue-level facts were unavailable from the runtime source.',
          status: 'blocked',
        });
      }
      facts.unavailable = {
        reason,
        prerequisites,
      };
    }

    const checks: AssistantPresentationFactCheck[] = [];
    const missingDocs = typeof queueSummary.applicationsMissingDocuments === 'number'
      ? queueSummary.applicationsMissingDocuments
      : 0;
    const highFlags = typeof queueSummary.highSeverityFlaggedCount === 'number'
      ? queueSummary.highSeverityFlaggedCount
      : 0;
    const agedPending = typeof queueSummary.agedPendingCount === 'number'
      ? queueSummary.agedPendingCount
      : 0;
    if (missingDocs > 0) {
      checks.push({
        id: 'financial-aid-check-documents',
        label: `Request missing documents for ${missingDocs} applications`,
        detail: typeof queueSummary.totalMissingDocuments === 'number'
          ? `${queueSummary.totalMissingDocuments} required documents are currently missing.`
          : null,
        status: 'watch',
      });
    }
    if (highFlags > 0) {
      checks.push({
        id: 'financial-aid-check-high-flags',
        label: `Review ${highFlags} high-severity flagged applications`,
        detail: 'High-severity flags should be cleared before award decisions.',
        status: 'blocked',
      });
    }
    if (agedPending > 0) {
      checks.push({
        id: 'financial-aid-check-aged-pending',
        label: `Prioritize ${agedPending} applications pending 14+ days`,
        detail: 'Oldest queue-age files are the strongest stale-review signal in the current source.',
        status: 'watch',
      });
    }
    if (checks.length === 0 && available) {
      checks.push({
        id: 'financial-aid-check-review-queue',
        label: 'Review highest-priority queue items first',
        detail: 'No stronger blocker signal was available from the current aggregate.',
        status: 'todo',
      });
    }
    facts.checks = checks;

    const priorityApplications = Array.isArray(queueSummary.priorityApplications)
      ? queueSummary.priorityApplications
      : [];
    facts.targets = priorityApplications.flatMap((entry, index) => {
      if (!isRecord(entry)) {
        return [];
      }
      const code = compactText(entry.code);
      const label = compactText(entry.familyName) || code || `Application ${index + 1}`;
      const href = compactText(entry.targetRoute);
      if (!label && !href) {
        return [];
      }
      return [{
        id: compactText(entry.id) || code || `financial-aid-target-${index + 1}`,
        label,
        eyebrow: 'Financial aid',
        badge: code || 'Application',
        meta: compactText(entry.status).replace(/_/g, ' ') || null,
        preview: compactText(entry.priorityReason) || null,
        href,
        actionLabel: 'Open application',
        detailRows: [
          typeof entry.daysPending === 'number'
            ? { label: 'Days pending', value: String(entry.daysPending) }
            : null,
          typeof entry.flagsOpen === 'number'
            ? { label: 'Open flags', value: String(entry.flagsOpen) }
            : null,
        ].filter((row): row is { label: string; value: string } => Boolean(row)),
      }];
    });
  }

  if (!facts.targets?.length) {
    const citedFinancialAid = input.citations.some((citation) => citation.table === 'FinancialAidApplications');
    if (citedFinancialAid) {
      facts.targets = [];
    }
  }

  return facts;
};

const inferAdmissionsPipelineFactsFromAnswer = (
  answer: string,
): AssistantPresentationFactBundle => {
  if (!/\bAdmissions Pipeline Bottlenecks\b/i.test(answer)) {
    return {};
  }
  const stageRows = new Map<string, number>();
  const stagePattern = /\b([A-Z][A-Za-z ]+?):\s*(\d+)\s+applicants\b/g;
  let match: RegExpExecArray | null;
  while ((match = stagePattern.exec(answer)) !== null) {
    const label = normalizePipelineStageLabel(match[1]);
    const count = Number(match[2]);
    if (label && Number.isFinite(count)) {
      stageRows.set(label, Math.max(stageRows.get(label) ?? 0, count));
    }
  }
  if (stageRows.size === 0) {
    return {};
  }
  const rows = [...stageRows.entries()];
  const tableColumns: AssistantPresentationTableColumn[] = [
    { id: 'stage', label: 'Stage' },
    { id: 'count', label: 'Count', align: 'right' },
    { id: 'signal', label: 'Signal' },
  ];
  const tableRows: AssistantPresentationTableRow[] = rows.map(([label, count]) => ({
    id: `stage-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    cells: [
      label,
      String(count),
      /Conversion rates?:\s*Unavailable/i.test(answer)
        ? 'Current-state count; not a transition-rate claim'
        : 'Current-stage count',
    ],
  }));
  const sourceLedger = {
    source: 'assistant-answer-repair',
    stageFunnel: tableRowsToRecords(tableColumns, tableRows),
  };
  return {
    chart: {
      id: 'admissions-stage-counts-chart',
      title: 'Pipeline stage chart',
      chartTitle: 'Applications by current stage',
      items: rows.slice(0, 8).map(([label, count], index) => ({
        id: `stage-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        label,
        value: count,
        tone: index === 0 ? 'accent' : 'primary',
      })),
    },
    table: {
      id: 'admissions-stage-bottleneck-table',
      title: 'Stage funnel',
      columns: tableColumns,
      rows: tableRows,
      pagination: {
        mode: 'auto',
        pageSize: 8,
        pageSizeOptions: [8, 16, 32],
        threshold: 8,
        itemLabel: 'Stage',
      },
      footerText: 'Conversion-rate percentages are intentionally omitted because current stage counts are not transition history.',
    },
    artifacts: [
      {
        id: 'admissions-pipeline-bottlenecks-csv',
        label: 'Stage funnel CSV',
        kind: 'csv',
        badge: 'CSV',
        meta: `${rows.length} stage rows`,
        preview: 'Stage, count, and bottleneck signal columns.',
        href: encodeTableCsvDataUrl(tableColumns, tableRows),
        sourceRef: 'admissions.pipeline.bottlenecks',
        actionLabel: 'Download CSV',
      },
      {
        id: 'admissions-pipeline-source-ledger-json',
        label: 'Source ledger JSON',
        kind: 'source-ledger',
        badge: 'JSON',
        meta: 'Applications aggregate',
        preview: 'Aggregate metadata for audit and rehydration.',
        href: encodeJsonDataUrl(sourceLedger),
        sourceRef: 'admissions.pipeline.source-ledger',
        actionLabel: 'Download JSON',
      },
    ],
  };
};

const inferAdmissionsPipelineFacts = (input: {
  debug?: Record<string, unknown> | null;
  answer: string;
}): AssistantPresentationFactBundle => {
  if (compactText(input.debug?.responseDecision) !== 'deterministic_pipeline_bottlenecks') {
    return inferAdmissionsPipelineFactsFromAnswer(input.answer);
  }
  const aggregate = isRecord(input.debug?.aggregateInsights) ? input.debug.aggregateInsights : null;
  const stageDistribution = readRecordArray(aggregate?.stageDistribution);
  const stageDwellDays = readRecordArray(aggregate?.stageDwellDays);
  const bottleneckStages = readRecordArray(aggregate?.bottleneckStages);
  const applicationCount = readNumber(isRecord(aggregate?.pipelineTotals) ? aggregate?.pipelineTotals.applications : 0);
  if (stageDistribution.length === 0) {
    return inferAdmissionsPipelineFactsFromAnswer(input.answer);
  }
  const dwellByStatus = new Map(stageDwellDays.map((entry) => [compactText(entry.status), entry] as const));
  const tableStatuses = [
    ...new Set([
      ...bottleneckStages.map((entry) => compactText(entry.status)),
      ...stageDistribution.map((entry) => compactText(entry.status)),
    ]),
  ].filter(Boolean);
  const tableColumns: AssistantPresentationTableColumn[] = [
    { id: 'stage', label: 'Stage' },
    { id: 'count', label: 'Count', align: 'right' },
    { id: 'dwell', label: 'Avg dwell', align: 'right' },
    { id: 'severity', label: 'Severity' },
    { id: 'signal', label: 'Signal' },
  ];
  const tableRows: AssistantPresentationTableRow[] = tableStatuses.map((status) => {
    const distribution = stageDistribution.find((entry) => compactText(entry.status) === status);
    const bottleneck = bottleneckStages.find((entry) => compactText(entry.status) === status);
    const dwell = dwellByStatus.get(status);
    return {
      id: `stage-row-${status}`,
      cells: [
        normalizePipelineStageLabel(status),
        String(readNumber(distribution?.count || bottleneck?.count || dwell?.count)),
        `${readNumber(bottleneck?.avgDwellDays || dwell?.avgDays)}d`,
        bottleneck ? normalizePipelineStageLabel(bottleneck.severity) : 'Normal',
        readRecordArray(bottleneck?.reasons).length > 0
          ? readRecordArray(bottleneck?.reasons).map(compactText).join('; ')
          : compactText(bottleneck?.reasons).replace(/,/g, '; ') || 'No configured bottleneck threshold exceeded',
      ],
    };
  });
  const sourceLedger = {
    source: 'admissions-aggregate-insights',
    applicationCount: applicationCount || stageDistribution.reduce((sum, entry) => sum + readNumber(entry.count), 0),
    stageDistribution,
    stageDwellDays,
    bottleneckStages,
    stageFunnel: tableRowsToRecords(tableColumns, tableRows),
  };
  return {
    metrics: [
      {
        id: 'applications-reviewed',
        label: 'Applications reviewed',
        value: String(applicationCount || stageDistribution.reduce((sum, entry) => sum + readNumber(entry.count), 0)),
        detail: 'Full authorized admissions dataset',
        tone: 'info',
      },
      {
        id: 'largest-stage',
        label: 'Largest stage',
        value: normalizePipelineStageLabel(stageDistribution[0]?.status),
        detail: `${readNumber(stageDistribution[0]?.count)} applications`,
        tone: 'warning',
      },
      {
        id: 'bottleneck-stages',
        label: 'Bottleneck stages',
        value: String(bottleneckStages.length),
        detail: bottleneckStages.map((entry) => normalizePipelineStageLabel(entry.status)).join(', ') || 'No stages exceed thresholds',
        tone: bottleneckStages.length > 0 ? 'warning' : 'success',
      },
      {
        id: 'conversion-rates',
        label: 'Conversion rates',
        value: 'Unavailable',
        detail: 'Current-state records are not cohort transition history',
        tone: 'neutral',
      },
    ],
    chart: {
      id: 'admissions-stage-counts-chart',
      title: 'Pipeline stage chart',
      chartTitle: 'Applications by current stage',
      items: stageDistribution.slice(0, 8).map((entry, index) => ({
        id: `stage-${compactText(entry.status) || index + 1}`,
        label: normalizePipelineStageLabel(entry.status),
        value: readNumber(entry.count),
        tone: index === 0 ? 'accent' : 'primary',
      })),
    },
    table: {
      id: 'admissions-stage-bottleneck-table',
      title: 'Stage funnel',
      columns: tableColumns,
      rows: tableRows,
      pagination: {
        mode: 'auto',
        pageSize: 8,
        pageSizeOptions: [8, 16, 32],
        threshold: 8,
        itemLabel: 'Stage',
      },
      footerText: 'Conversion-rate percentages are intentionally omitted because current stage counts are not transition history.',
    },
    artifacts: [
      {
        id: 'admissions-pipeline-bottlenecks-csv',
        label: 'Stage funnel CSV',
        kind: 'csv',
        badge: 'CSV',
        meta: `${tableStatuses.length} stage rows`,
        preview: 'Stage, count, dwell, severity, and bottleneck reason columns.',
        href: encodeTableCsvDataUrl(tableColumns, tableRows),
        sourceRef: 'admissions.pipeline.bottlenecks',
        actionLabel: 'Download CSV',
      },
      {
        id: 'admissions-pipeline-source-ledger-json',
        label: 'Source ledger JSON',
        kind: 'source-ledger',
        badge: 'JSON',
        meta: 'Applications aggregate',
        preview: 'Full-dataset aggregate metadata for audit and rehydration.',
        href: encodeJsonDataUrl(sourceLedger),
        sourceRef: 'admissions.pipeline.source-ledger',
        actionLabel: 'Download JSON',
      },
    ],
  };
};

const inferResponseKind = (input: {
  facts: AssistantPresentationFactBundle;
  hasResultCards: boolean;
  hasTask: boolean;
  hasArtifact: boolean;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
  projectedSections: AssistantPresentationSection[];
  mainText: string;
  readOnlyDegradedReason: string | null;
}): AssistantResponseKind | null => {
  if (input.facts.unavailable || input.readOnlyDegradedReason) {
    return 'data_unavailable';
  }
  if (input.facts.chart && input.facts.table && ((input.facts.artifacts?.length ?? 0) > 0 || (input.facts.workspace?.length ?? 0) > 0)) {
    return 'multi_artifact_insight';
  }
  if (input.hasArtifact || (input.facts.artifacts?.length ?? 0) > 0 || (input.facts.workspace?.length ?? 0) > 0) {
    if (input.artifactPreview?.landingPageArtifact) {
      return 'landing_page_artifact';
    }
    if (input.artifactPreview?.diagramArtifact) {
      return 'diagram_artifact';
    }
    if (input.projectedSections.some((section) => section.type === 'chart') || input.facts.chart) {
      return 'chart_artifact';
    }
    return 'spreadsheet_artifact';
  }
  if (input.hasTask) {
    return 'proactive_guide';
  }
  if ((input.facts.checks?.length ?? 0) > 0) {
    return 'todo_list';
  }
  if (input.hasResultCards || (input.facts.targets?.length ?? 0) > 0) {
    return 'navigation_assistance';
  }
  if (input.projectedSections.some((section) => section.type === 'table')) {
    return 'navigation_assistance';
  }
  if (input.facts.chart || input.projectedSections.some((section) => section.type === 'chart')) {
    return 'chart_artifact';
  }
  if (input.facts.table) {
    return 'text_summary';
  }
  if (/\b(draft|message|email|letter)\b/i.test(input.mainText)) {
    return 'draft_creator';
  }
  return null;
};

export const composeAssistantPresentation = (
  input: AssistantPresentationCompositionInput,
): AssistantPresentationCompositionResult => {
  const routeFamilyId = input.routeFamilyId
    ?? (input.pathname ? resolveAssistantRouteFamily(input.pathname).id : input.module);
  const { sanitizedText, inlineLinks } = stripInlineAssistantLinks(input.answer);
  const responseSections = splitAssistantResponseSections(sanitizedText);
  const relatedLinkGroups = buildAssistantLinkGroups({
    citations: input.citations,
    actions: input.actions,
    inlineLinks,
  });
  const sources = buildAssistantSources(input.citations);
  const generatedResultCards = shouldUseExactTargetCards(input.debug)
    ? buildAssistantResultCards({
        citations: input.citations,
        actions: input.actions,
      })
    : [];
  const inferredFacts = input.module === 'financial-aid'
    ? inferFinancialAidFacts({
        debug: input.debug,
        actions: input.actions,
        citations: input.citations,
      })
    : input.module === 'admissions'
      ? inferAdmissionsPipelineFacts({
          debug: input.debug,
          answer: responseSections.mainText,
        })
    : {};
  const facts = mergeFacts(input.facts, inferredFacts);
  const factsCarryArtifacts = Boolean((facts.artifacts?.length ?? 0) > 0 || (facts.workspace?.length ?? 0) > 0);
  const responseSurfaceProjection = input.projectedSections
    ? {
        mainText: responseSections.mainText,
        projectedSections: input.projectedSections,
        suppressRelatedLinks: input.suppressRelatedLinks === true,
        rawDataText: null,
      }
    : factsCarryArtifacts
    ? {
        mainText: responseSections.mainText,
        projectedSections: [] as AssistantPresentationSection[],
        suppressRelatedLinks: false,
        rawDataText: null,
      }
    : projectAdmissionsResponseSurface({
        mainText: responseSections.mainText,
        citations: input.citations,
        actions: input.actions,
        artifactPreview: input.artifactPreview ?? null,
      });
  const factSections = buildFactSections({
    facts,
    includeFactTargets: generatedResultCards.length === 0,
  });
  const hasArtifact = Boolean(
    input.artifactPreview?.workbookArtifact
    || input.artifactPreview?.spreadsheetDownloadArtifact
    || input.artifactPreview?.chartArtifact
    || input.artifactPreview?.landingPageArtifact
    || input.artifactPreview?.diagramArtifact
    || (input.artifactPreview?.artifactCards?.length ?? 0) > 0
    || (input.artifactPreview?.chartPanels?.length ?? 0) > 0
  );
  const responseKind = inferResponseKind({
    facts,
    hasResultCards: generatedResultCards.length > 0,
    hasTask: Boolean(input.taskProposal || input.taskThread),
    hasArtifact,
    artifactPreview: input.artifactPreview ?? null,
    projectedSections: [...responseSurfaceProjection.projectedSections, ...factSections],
    mainText: responseSurfaceProjection.mainText,
    readOnlyDegradedReason: input.readOnlyDegradedReason ?? null,
  });
  const factsAddChecklist = Boolean((facts.checks?.length ?? 0) > 0 || facts.unavailable?.prerequisites.length);
  const summaryText = facts.unavailable
    ? firstSentence(facts.unavailable.reason.replace(/_/g, ' ')) || firstSentence(responseSurfaceProjection.mainText)
    : factsAddChecklist
      ? stripChecklistBlob(responseSurfaceProjection.mainText)
      : responseSurfaceProjection.mainText;
  const presentation = buildAdmissionsAssistantPresentationModel({
    module: input.module,
    routeFamily: routeFamilyId,
    mainText: summaryText,
    explanationText: responseSections.explanationText,
    citations: input.citations,
    actions: input.actions,
    sources,
    relatedLinkGroups,
    resultCards: generatedResultCards,
    artifactPreview: input.artifactPreview ?? null,
    taskProposal: input.taskProposal ?? null,
    taskThread: input.taskThread ?? null,
    taskThreadRollup: input.taskThreadRollup ?? null,
    presentationDiagnostics: input.diagnostics ?? null,
    readOnlyDegradedReason: input.readOnlyDegradedReason ?? null,
    settingsSpecialistSnapshot: input.settingsSpecialistSnapshot ?? null,
    projectedSections: [
      ...factSections,
      ...responseSurfaceProjection.projectedSections,
    ],
    suppressRelatedLinks: responseSurfaceProjection.suppressRelatedLinks || generatedResultCards.length > 0,
    responseKind,
  });
  const normalizedPresentation = normalizeAssistantPresentation(presentation) ?? presentation;
  const visibleSectionTypes = resolveVisibleSectionTypes(normalizedPresentation.sections);
  const supportSectionTypes = resolveSupportSectionTypes(normalizedPresentation.sections);
  const visibleWithoutSummary = visibleSectionTypes.filter((type) => type !== 'summary');
  const contractDiagnostics = {
    serverSectionTypes: sectionTypes(normalizedPresentation.sections),
    visibleSectionTypes,
    supportSectionTypes,
    lowStructure: visibleWithoutSummary.length === 0,
    supportOnlyStructure: visibleWithoutSummary.length === 0 && supportSectionTypes.length > 0,
  };

  return {
    presentation: normalizedPresentation,
    responseKind: normalizedPresentation.responseKind ?? responseKind ?? 'text_summary',
    visibleSectionTypes,
    supportSectionTypes,
    contractDiagnostics,
    clientProjectionUsed: input.clientProjectionUsed === true,
  };
};
